// Simple logger implementation that can be used in both client and server
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

interface LogEntry {
  level: LogLevel;
  message: string;
  category?: string;
  data?: unknown;
  timestamp: Date;
}

interface LoggerOptions {
  level?: LogLevel;
  maxLogs?: number;
  enabledCategories?: string[];
  defaultCategory?: string;
}

declare const window: Window & typeof globalThis;

export class Logger {
  private level: LogLevel;
  private recentLogs: LogEntry[] = [];
  private maxLogs: number;
  private enabled: boolean;
  private isWorker: boolean;
  private enabledCategories: Set<string>;
  private defaultCategory: string;

  constructor(options: LoggerOptions = {}) {
    const {
      level = LogLevel.INFO,
      maxLogs = 100,
      enabledCategories = [],
      defaultCategory = 'app',
    } = options;

    this.level = level;
    this.maxLogs = maxLogs;
    this.enabled = true;
    this.enabledCategories = new Set(enabledCategories);
    this.defaultCategory = defaultCategory;

    // Check if we're in a worker environment
    try {
      this.isWorker = typeof window === 'undefined';
    } catch {
      this.isWorker = true;
    }
  }

  setLevel(level: LogLevel): void {
    this.level = level;
  }

  getLevel(): LogLevel {
    return this.level;
  }

  enable(): void {
    this.enabled = true;
  }

  disable(): void {
    this.enabled = false;
  }

  enableCategory(category: string): void {
    this.enabledCategories.add(category);
  }

  disableCategory(category: string): void {
    this.enabledCategories.delete(category);
  }

  setEnabledCategories(categories: string[]): void {
    this.enabledCategories = new Set(categories);
  }

  isCategoryEnabled(category?: string): boolean {
    if (!category) return true;
    return this.enabledCategories.size === 0 || this.enabledCategories.has(category);
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  clearLogs(): void {
    this.recentLogs = [];
  }

  getRecentLogs(category?: string): LogEntry[] {
    if (!category) return [...this.recentLogs];
    return this.recentLogs.filter((log) => log.category === category);
  }

  debug(message: string, category?: string, data?: unknown): void {
    this.log(LogLevel.DEBUG, message, category, data);
  }

  info(message: string, category?: string, data?: unknown): void {
    this.log(LogLevel.INFO, message, category, data);
  }

  warn(message: string, category?: string, data?: unknown): void {
    this.log(LogLevel.WARN, message, category, data);
  }

  error(message: string, category?: string, data?: unknown): void {
    this.log(LogLevel.ERROR, message, category, data);
  }

  // Helper methods for common error logging patterns
  errorWithData(message: string, data: unknown, category?: string): void {
    this.log(LogLevel.ERROR, message, category, data);
  }

  errorWithException(message: string, error: unknown, category?: string): void {
    const errorData =
      error instanceof Error
        ? { name: error.name, message: error.message, stack: error.stack }
        : error;
    this.log(LogLevel.ERROR, message, category, errorData);
  }

  private log(level: LogLevel, message: string, category?: string, data?: unknown): void {
    const actualCategory = category || this.defaultCategory;

    if (!this.enabled || level < this.level || !this.isCategoryEnabled(actualCategory)) return;

    const entry: LogEntry = {
      level,
      message,
      category: actualCategory,
      data,
      timestamp: new Date(),
    };

    this.recentLogs.unshift(entry);
    if (this.recentLogs.length > this.maxLogs) {
      this.recentLogs.pop();
    }

    const time = entry.timestamp.toLocaleTimeString();
    const levelName = LogLevel[level];
    const prefix = `[${time}] [${levelName}] [${actualCategory}]`;

    if (this.isWorker) {
      // Worker environment - simple console output
      const logMethod =
        level === LogLevel.ERROR
          ? console.error
          : level === LogLevel.WARN
            ? console.warn
            : level === LogLevel.INFO
              ? console.info
              : console.debug;

      data ? logMethod(prefix, message, data) : logMethod(prefix, message);
    } else {
      // Browser environment - styled console output
      const style =
        level === LogLevel.ERROR
          ? 'color: #dc3545'
          : level === LogLevel.WARN
            ? 'color: #fd7e14'
            : level === LogLevel.INFO
              ? 'color: #0d6efd'
              : 'color: #6c757d';

      data
        ? console.log(`%c${prefix} ${message}`, style, data)
        : console.log(`%c${prefix} ${message}`, style);
    }
  }
}

// Create default instance with production settings
export const logger = new Logger({
  level: process.env.NODE_ENV === 'production' ? LogLevel.INFO : LogLevel.DEBUG,
  defaultCategory: 'app',
});

export default logger;
